import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Alert,
    LinearProgress,
    Chip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventIcon from '@mui/icons-material/Event';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider
} from '@mui/material';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface Evaluation {
    _id: string;
    title: string;
    blockId: {
        _id: string;
        title: string;
    };
    timeLimitMinutes: number;
    statementText: string;
    statementFiles: string[];
}

interface EvaluationStatus {
    _id: string;
    answers: string;
    files?: Array<{ name: string, url: string, type: string }>;
    startedAt: string;
    deadline: string;
    submittedAt: string | null;
}

interface UploadedFile {
    name: string;
    url: string;
    type: string;
}

const LearnerEvaluationPage: React.FC = () => {
    const evaluationId = useParams<{ evaluationId: string }>().evaluationId;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
    const [status, setStatus] = useState<EvaluationStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [answer, setAnswer] = useState('');
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        fetchEvaluation();
        fetchStatus();
    }, [evaluationId]);

    useEffect(() => {
        if (!status || !status.deadline || submitted || status.submittedAt) return;

        const updateTimer = () => {
            const now = new Date().getTime();
            const deadline = new Date(status.deadline).getTime();
            const remaining = Math.max(0, Math.floor((deadline - now) / 1000));

            setTimeRemaining(remaining);

            if (remaining <= 0 && !submitted) {
                handleAutoSubmit();
            }
        };

        updateTimer();
        const timer = setInterval(updateTimer, 1000);

        return () => clearInterval(timer);
    }, [status, submitted]);

    const fetchEvaluation = async () => {
        try {
            const res = await api.get(`/api/block-evaluations/${evaluationId}`);
            setEvaluation(res.data);
        } catch (err) {
            console.error('Error fetching evaluation', err);
            showToast('Erreur lors du chargement de l\'évaluation', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatus = async () => {
        try {
            const res = await api.get(`/api/block-evaluations/${evaluationId}/status`);
            if (res.data) {
                setStatus(res.data);
                setAnswer(res.data.answers || '');
                setFiles(res.data.files || []);
                if (res.data.submittedAt) {
                    setSubmitted(true);
                }
            }
        } catch (err) {
            console.error('Error fetching status', err);
        }
    };

    const handleStart = async () => {
        try {
            const res = await api.post(`/api/block-evaluations/${evaluationId}/start`);
            setStatus(res.data);
            showToast('Évaluation démarrée', 'success');
        } catch (err) {
            console.error('Error starting evaluation', err);
            showToast('Erreur lors du démarrage', 'error');
        }
    };

    const handleSaveAndQuit = async () => {
        try {
            await api.post(`/api/block-evaluations/${evaluationId}/draft`, {
                answers: answer,
                files
            });
            showToast('Brouillon sauvegardé', 'success');
            navigate('/learner/evaluations');
        } catch (err) {
            console.error('Error saving draft', err);
            showToast('Erreur lors de la sauvegarde', 'error');
        }
    };

    const handleSubmit = async () => {
        if (!answer.trim() && files.length === 0) {
            showToast('Veuillez saisir une réponse ou ajouter un fichier', 'warning');
            return;
        }

        try {
            await api.post(`/api/block-evaluations/${evaluationId}/submissions`, {
                answers: answer,
                files
            });
            showToast('Évaluation soumise avec succès', 'success');
            setSubmitted(true);
            setTimeout(() => navigate('/learner/evaluations'), 2000);
        } catch (err) {
            console.error('Error submitting evaluation', err);
            showToast('Erreur lors de la soumission', 'error');
        }
    };

    const handleAutoSubmit = async () => {
        if (submitted) return;

        try {
            await api.post(`/api/block-evaluations/${evaluationId}/submissions`, {
                answers: answer || 'Temps écoulé - Réponse non complétée',
                files
            });
            showToast('Temps écoulé - Évaluation soumise automatiquement', 'warning');
            setSubmitted(true);
            setTimeout(() => navigate('/learner/evaluations'), 2000);
        } catch (err) {
            console.error('Error auto-submitting evaluation', err);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles: UploadedFile[] = [];

            for (let i = 0; i < e.target.files.length; i++) {
                const file = e.target.files[i];
                try {
                    const base64 = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                    });

                    newFiles.push({
                        name: file.name,
                        url: base64,
                        type: file.type
                    });
                } catch (err) {
                    console.error('Error reading file', err);
                    showToast(`Erreur lors de la lecture du fichier ${file.name}`, 'error');
                }
            }

            setFiles(prev => [...prev, ...newFiles]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const formatTimeAllocation = (minutes: number): string => {
        const days = Math.floor(minutes / 1440);
        const hours = Math.floor((minutes % 1440) / 60);
        const mins = minutes % 60;

        const parts = [];
        if (days > 0) parts.push(`${days} jour${days > 1 ? 's' : ''}`);
        if (hours > 0) parts.push(`${hours} heure${hours > 1 ? 's' : ''}`);
        if (mins > 0) parts.push(`${mins} minute${mins > 1 ? 's' : ''}`);

        return parts.join(', ') || '0 minute';
    };

    const formatTimeRemaining = (seconds: number): string => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (days > 0) {
            return `${days}j ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`;
        }
        if (hours > 0) {
            return `${hours}h ${minutes.toString().padStart(2, '0')}m ${secs.toString().padStart(2, '0')}s`;
        }
        return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
    };

    const formatDeadline = (deadline: string): string => {
        const date = new Date(deadline);
        return date.toLocaleString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTimeColor = (): string => {
        if (!status || !evaluation) return '#10b981';
        const totalSeconds = evaluation.timeLimitMinutes * 60;
        const percentage = (timeRemaining / totalSeconds) * 100;

        if (percentage > 50) return '#10b981';
        if (percentage > 25) return '#f59e0b';
        return '#ef4444';
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
                <Typography>Chargement...</Typography>
            </Box>
        );
    }

    if (!evaluation) {
        return (
            <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
                <Alert severity="error">Évaluation introuvable</Alert>
            </Box>
        );
    }

    // Not started yet
    if (!status || !status.startedAt) {
        return (
            <Box sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
                <Paper sx={{
                    p: 4,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}>
                    <Typography variant="h4" sx={{ mb: 3, fontFamily: 'Quicksand, sans-serif', fontWeight: 700 }}>
                        {evaluation.title}
                    </Typography>

                    <Chip
                        label={evaluation.blockId.title}
                        sx={{ mb: 3, fontFamily: 'Quicksand, sans-serif' }}
                    />

                    <Alert severity="info" sx={{ mb: 3, fontFamily: 'Quicksand, sans-serif' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Temps alloué :</strong> {formatTimeAllocation(evaluation.timeLimitMinutes)}
                        </Typography>
                        <Typography variant="body2">
                            Une fois démarré, le chronomètre ne peut pas être mis en pause. Vous pouvez quitter et revenir à tout moment.
                        </Typography>
                    </Alert>

                    <Alert severity="warning" sx={{ mb: 3, fontFamily: 'Quicksand, sans-serif' }}>
                        <Typography variant="body2">
                            L'énoncé et les fichiers ne seront visibles qu'après avoir démarré l'évaluation.
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/learner/evaluations')}
                            sx={{ fontFamily: 'Quicksand, sans-serif', textTransform: 'none' }}
                        >
                            Retour
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleStart}
                            sx={{
                                fontFamily: 'Quicksand, sans-serif',
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                px: 4
                            }}
                        >
                            Démarrer l'évaluation
                        </Button>
                    </Box>
                </Paper>
            </Box>
        );
    }

    // Evaluation started
    return (
        <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Timer Header */}
            <Paper sx={{
                p: { xs: 2, sm: 3 },
                mb: 3,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${getTimeColor()} 0%, ${getTimeColor()}dd 100%)`,
                color: 'white',
                boxShadow: `0 8px 32px ${getTimeColor()}40`
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 0 },
                    mb: 2
                }}>
                    <Typography variant="h5" sx={{
                        fontFamily: 'Quicksand, sans-serif',
                        fontWeight: 700,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }}>
                        {evaluation.title}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 2 },
                        alignSelf: { xs: 'center', sm: 'auto' }
                    }}>
                        <AccessTimeIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
                        <Typography variant="h4" sx={{
                            fontFamily: 'Quicksand, sans-serif',
                            fontWeight: 700,
                            fontSize: { xs: '1.75rem', sm: '2.125rem' }
                        }}>
                            {formatTimeRemaining(timeRemaining)}
                        </Typography>
                    </Box>
                </Box>

                {/* Deadline Display */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, opacity: 0.95 }}>
                    <EventIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    <Typography variant="body2" sx={{ fontFamily: 'Quicksand, sans-serif', fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                        <strong>Date limite :</strong> {formatDeadline(status.deadline)}
                    </Typography>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={(timeRemaining / (evaluation.timeLimitMinutes * 60)) * 100}
                    sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.3)',
                        '& .MuiLinearProgress-bar': {
                            bgcolor: 'white'
                        }
                    }}
                />
            </Paper>

            {/* Evaluation Content */}
            <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
                    Énoncé
                </Typography>
                <Box sx={{ mb: 3, p: { xs: 2, sm: 3 }, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Typography variant="body1" sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'Quicksand, sans-serif',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}>
                        {evaluation.statementText}
                    </Typography>
                </Box>

                {evaluation.statementFiles && evaluation.statementFiles.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
                            Fichiers joints :
                        </Typography>
                        {evaluation.statementFiles.map((file, idx) => (
                            <Box key={idx} sx={{ mb: 1 }}>
                                <a href={file} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                                    📎 Fichier {idx + 1}
                                </a>
                            </Box>
                        ))}
                    </Box>
                )}

                <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
                    Votre réponse
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={15}
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Saisissez votre réponse ici..."
                    disabled={submitted}
                    sx={{
                        mb: 3,
                        '& .MuiInputBase-input': {
                            fontFamily: 'Quicksand, sans-serif'
                        }
                    }}
                />

                {/* File Upload Section */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
                        Fichiers joints à votre réponse
                    </Typography>

                    {!submitted && (
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<AttachFileIcon />}
                            sx={{
                                mb: 2,
                                fontFamily: 'Quicksand, sans-serif',
                                textTransform: 'none',
                                borderStyle: 'dashed'
                            }}
                        >
                            Ajouter un fichier
                            <input
                                type="file"
                                hidden
                                multiple
                                onChange={handleFileChange}
                            />
                        </Button>
                    )}

                    {files.length > 0 && (
                        <List sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                            {files.map((file, index) => (
                                <React.Fragment key={index}>
                                    <ListItem>
                                        <AttachFileIcon sx={{ mr: 2, color: '#64748b' }} />
                                        <ListItemText
                                            primary={file.name}
                                            primaryTypographyProps={{ fontFamily: 'Quicksand, sans-serif' }}
                                        />
                                        {!submitted && (
                                            <ListItemSecondaryAction>
                                                <IconButton edge="end" onClick={() => handleRemoveFile(index)} size="small">
                                                    <DeleteIcon color="error" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        )}
                                    </ListItem>
                                    {index < files.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>

                {submitted ? (
                    <Alert severity="success" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                        Évaluation soumise avec succès ! Redirection en cours...
                    </Alert>
                ) : (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2
                    }}>
                        <Button
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveAndQuit}
                            fullWidth={isMobile}
                            sx={{ fontFamily: 'Quicksand, sans-serif', textTransform: 'none' }}
                        >
                            Sauvegarder et quitter
                        </Button>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            fullWidth={isMobile}
                            sx={{
                                fontFamily: 'Quicksand, sans-serif',
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                px: 4,
                                py: 1.5
                            }}
                        >
                            Soumettre l'évaluation
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default LearnerEvaluationPage;
